import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import {
  GetDirectMembershipsForFacepile,
  GetSuggestedMembers,
} from '@frameio/graphql-client/generated';
import { useClearFocusedAsset } from '@frameio/selection';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import {
  AlertDialog,
  AlertDialogButton,
  ToastAlert,
  useToast,
} from '@frameio/vapor';
import { useSetFolderRestrictedMutation } from '../gql/generated/SetFolderRestricted';
import TEXT from './generated/TEXT/useRestrictFolder';

type RestrictFolderArgs = {
  folderId: string;
  force?: boolean;
  restricted: boolean;
};

export type RestrictFolderReturn = {
  restrictFolder: (args: RestrictFolderArgs) => void;
  restrictWarningDialog: ReactNode;
};

type WarningState = 'hidden' | 'restricting' | 'unrestricting';

/**
 * The useRestrictFolder hook can be used to toggle the restricted status of a
 * folder.
 *
 * @returns callback The callback to fire to update restricted status
 */
export function useRestrictFolder(): RestrictFolderReturn {
  const [setFolderRestricted] = useSetFolderRestrictedMutation();
  const [warningState, setWarningState] = useState<WarningState>('hidden');
  const restrictArgs = useRef<RestrictFolderArgs | null>(null);
  const leastDestructiveRef = useRef(null);
  const clearFocusedAsset = useClearFocusedAsset();
  const { addToast } = useToast();
  const track = useTrackUnsafe();
  const accountId = useActiveAccountId();

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textError = TEXT.error;

  const restrictFolderCallback = useCallback(
    ({ folderId, force = false, restricted }: RestrictFolderArgs) => {
      if (!force) {
        restrictArgs.current = {
          folderId,
          restricted,
        };
        setWarningState(restricted ? 'restricting' : 'unrestricting');
        return;
      }

      track('folder-restriction-updated', {
        asset_id: folderId,
        is_restricted: restricted,
      });

      setFolderRestricted({
        variables: {
          input: {
            folderId,
            restricted,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          setFolderRestricted: {
            __typename: 'SetFolderRestrictedPayload',
            folderAsset: {
              __typename: 'FolderAsset',
              id: folderId,
              restricted,
            },
          },
        },

        onError() {
          addToast((closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={textError}
              variant="error"
            />
          ));
        },
        refetchQueries: [
          {
            query: GetSuggestedMembers,
            variables: {
              filter: {
                query: '',
              },
              resourceId: folderId,
              orderBy: {
                field: 'NAME',
                direction: 'ASC_NULLS_LAST',
              },
              page: {
                first: DEFAULT_INITIAL_PAGE_SIZE,
              },
            },
          },
          {
            query: GetDirectMembershipsForFacepile,
            variables: {
              accountId,
              resourceId: folderId,
            },
          },
        ],
      });
    },
    [accountId, addToast, setFolderRestricted, textError, track]
  );
  const restrictWarningTitle = TEXT.restrictingTitle;
  const restrictWarningDetails = TEXT.restrictingDetails;
  const restrictWarningConfirm = TEXT.continue;
  const restrictWarningCancel = TEXT.cancel;
  const restrictWarningDialog = useMemo(
    () => (
      <AlertDialog
        actions={
          <>
            <AlertDialogButton
              onClick={() => {
                setWarningState('hidden');
              }}
            >
              {restrictWarningCancel}
            </AlertDialogButton>
            <AlertDialogButton
              color="primary"
              onClick={() => {
                if (!restrictArgs.current) return;

                restrictFolderCallback({
                  ...restrictArgs.current,
                  force: true,
                });
                setWarningState('hidden');
              }}
            >
              {restrictWarningConfirm}
            </AlertDialogButton>
          </>
        }
        details={restrictWarningDetails}
        isShowing={warningState === 'restricting'}
        leastDestructiveRef={leastDestructiveRef}
        onExited={() => {
          restrictArgs.current = null;
          clearFocusedAsset();
        }}
        title={restrictWarningTitle}
        variant="notice"
      />
    ),
    [
      clearFocusedAsset,
      restrictFolderCallback,
      warningState,
      restrictWarningCancel,
      restrictWarningConfirm,
      restrictWarningDetails,
      restrictWarningTitle,
    ]
  );

  const unrestrictWarningTitle = TEXT.unrestrictingTitle;
  const unrestrictWarningDetails = TEXT.unrestrictingDetails;
  const unrestrictWarningConfirm = TEXT.removeRestriction;
  const unrestrictWarningCancel = TEXT.keepRestriction;
  const unrestrictWarningDialog = useMemo(
    () => (
      <AlertDialog
        actions={
          <>
            <AlertDialogButton
              color="primary"
              onClick={() => {
                if (!restrictArgs.current) return;

                restrictFolderCallback({
                  ...restrictArgs.current,
                  force: true,
                });
                setWarningState('hidden');
              }}
            >
              {unrestrictWarningConfirm}
            </AlertDialogButton>
            <AlertDialogButton
              onClick={() => {
                setWarningState('hidden');
              }}
            >
              {unrestrictWarningCancel}
            </AlertDialogButton>
          </>
        }
        details={unrestrictWarningDetails}
        isShowing={warningState !== 'hidden'}
        leastDestructiveRef={leastDestructiveRef}
        onExited={() => {
          restrictArgs.current = null;
          clearFocusedAsset();
        }}
        stackActionButtons={true}
        title={unrestrictWarningTitle}
        variant="notice"
      />
    ),
    [
      clearFocusedAsset,
      restrictFolderCallback,
      warningState,
      unrestrictWarningCancel,
      unrestrictWarningConfirm,
      unrestrictWarningDetails,
      unrestrictWarningTitle,
    ]
  );

  return {
    restrictFolder: restrictFolderCallback,
    restrictWarningDialog:
      warningState === 'unrestricting'
        ? unrestrictWarningDialog
        : restrictWarningDialog,
  };
}

TEXT.DEFINE({
  cancel: 'Cancel',
  continue: 'Make Restricted',
  error: 'Something went wrong',
  keepRestriction: 'Keep Restriction',
  removeRestriction: 'Remove Restriction',
  restrictingDetails:
    'Only people directly invited will have access, plus admins. Any assets from this folder that have been added to Shares will be removed.',
  restrictingTitle: 'Are you sure you want to make this folder restricted?',
  unrestrictingDetails:
    'All project members will now have access. The folder and its assets can be visible in Shares.',
  unrestrictingTitle:
    'Are you sure you want to remove restriction from this folder?',
});
